import { tg } from "./telegram";
import type {
  BoogaDetail,
  ChallengeCreated,
  ChallengePreview,
  DirectoryEntry,
  DirectoryStatus,
  FightResponse,
  LeaderboardEntry,
  LeaderboardPeriod,
  LobbyChallenge,
  PvpFightResponse,
  PvpLeaderboardEntry,
  QueueJoinResult,
  QueueStatusResult,
  RosterResult,
  TournamentResponse,
} from "./types";

// API base. Default is SAME-ORIGIN (empty prefix → relative /roster, /fight…):
// the backend serves webapp/dist and the API on one origin (single-tunnel dev
// and any same-origin deploy), so a build with no env works from any device.
// Set VITE_API_BASE only when the API lives on another origin — e.g. the
// standalone Vite dev server (http://localhost:8080) or prod's split
// subdomains (https://api.devpops.in). A baked-in localhost base is the
// classic "works on my laptop, phone says Failed to fetch" trap.
const BASE = import.meta.env.VITE_API_BASE ?? "";

// The session bearer token from /auth/login (v2). Held in memory + sessionStorage
// so a reload inside the Mini App keeps the session until it expires. The token
// carries the EPHEMERAL verified address; we never store a raw wallet ourselves.
const SESSION_KEY = "bb_session";
let bearer: string | null =
  (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY)) || null;

export function setSession(token: string): void {
  bearer = token;
  try {
    sessionStorage.setItem(SESSION_KEY, token);
  } catch {
    /* storage blocked — in-memory token still works for this session */
  }
}

export function clearSession(): void {
  bearer = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function hasSession(): boolean {
  return bearer !== null;
}

// The Telegram-link token: proof of WHO on Telegram this browser belongs to,
// claimed once from a bot-issued login link (?tg=CODE) and kept for weeks in
// localStorage. Carries no wallet — the wallet is re-proven on every login.
const TGLINK_KEY = "bb_tglink";
export interface TgLinkInfo {
  token: string;
  username: string | null;
  /** unix seconds */
  expiresAt: number;
}
export function getTgLink(): TgLinkInfo | null {
  try {
    const raw = localStorage.getItem(TGLINK_KEY);
    if (!raw) return null;
    const info = JSON.parse(raw) as TgLinkInfo;
    if (!info.token || info.expiresAt * 1000 <= Date.now()) return null;
    return info;
  } catch {
    return null;
  }
}
export function setTgLink(info: TgLinkInfo | null): void {
  try {
    if (info) localStorage.setItem(TGLINK_KEY, JSON.stringify(info));
    else localStorage.removeItem(TGLINK_KEY);
  } catch {
    /* storage blocked — the link is lost on reload, the bot can issue another */
  }
}

/**
 * Headers for every API call. Forwards the session bearer (when logged in) and
 * the Telegram initData — the latter so the backend can tag logs with the acting
 * user. Empty outside Telegram (dev in a browser).
 */
function headers(extra?: HeadersInit): HeadersInit {
  const h: Record<string, string> = { "content-type": "application/json" };
  if (bearer) h["authorization"] = `Bearer ${bearer}`;
  if (tg?.initData) h["x-telegram-init-data"] = tg.initData;
  return { ...h, ...(extra as Record<string, string> | undefined) };
}

/** Error carrying the HTTP status so callers can react to 401 (expired session). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: headers(init?.headers),
  });
  const body = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const message =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return body as T;
}

export const api = {
  /**
   * Exchange a native-auth token (from sdk-dapp) + proof of Telegram identity
   * (the Telegram-link token; legacy initData still accepted) for a server
   * session; store the bearer for subsequent calls. Throws on 401.
   */
  async login(
    nativeAuthToken: string,
    identity: { tgLink?: string; initData?: string },
  ): Promise<{ address: string }> {
    const res = await request<{ token: string; address: string; expiresAt: number }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ nativeAuthToken, ...identity }) },
    );
    setSession(res.token);
    return { address: res.address };
  },

  /** DEV ONLY (server flag): log in as any wallet without proof — local PvP tests. */
  async devLogin(address: string): Promise<{ address: string }> {
    const res = await request<{ token: string; address: string; expiresAt: number }>(
      "/auth/dev-login",
      { method: "POST", body: JSON.stringify({ address }) },
    );
    setSession(res.token);
    return { address: res.address };
  },

  /** Claim a bot-issued one-time login code (`?tg=CODE`) for a Telegram link. */
  async claimTgCode(code: string): Promise<TgLinkInfo> {
    const res = await request<{ tgLink: string; username: string | null; expiresAt: number }>(
      "/auth/tg/claim",
      { method: "POST", body: JSON.stringify({ code }) },
    );
    const info = { token: res.tgLink, username: res.username, expiresAt: res.expiresAt };
    setTgLink(info);
    return info;
  },

  /**
   * Roster for the logged-in session's wallet (no argument — the server derives
   * the address from the session). Returns ok:false (not an error) for an SC or
   * empty wallet.
   */
  async roster(): Promise<RosterResult> {
    const res = await fetch(`${BASE}/roster`, {
      method: "POST",
      headers: headers(),
      // Empty JSON body: the route is session-derived, but Fastify's JSON parser
      // rejects an empty body when content-type is application/json.
      body: "{}",
    });
    return (await res.json()) as RosterResult;
  },

  fight(a: string, b: string, seed?: number): Promise<FightResponse> {
    return request<FightResponse>("/fight", {
      method: "POST",
      body: JSON.stringify({ a, b, seed }),
    });
  },

  poolFight(id: string, seed?: number): Promise<FightResponse> {
    return request<FightResponse>("/fight/pool", {
      method: "POST",
      body: JSON.stringify({ id, seed }),
    });
  },

  tournament(ids: string[], seed?: number): Promise<TournamentResponse> {
    return request<TournamentResponse>("/tournament", {
      method: "POST",
      body: JSON.stringify({ ids, seed }),
    });
  },

  leaderboard(
    period: LeaderboardPeriod = "all",
    limit = 25,
    offset = 0,
  ): Promise<LeaderboardEntry[]> {
    return request<LeaderboardEntry[]>(
      `/leaderboard?period=${period}&limit=${limit}&offset=${offset}`,
    );
  },

  boogaDetail(id: string): Promise<BoogaDetail> {
    return request<BoogaDetail>(`/booga/${encodeURIComponent(id)}`);
  },

  // --- PvP (Phase 2) ---------------------------------------------------------

  createChallenge(boogaId: string, targetTgId?: number): Promise<ChallengeCreated> {
    return request<ChallengeCreated>("/challenge", {
      method: "POST",
      body: JSON.stringify({ boogaId, targetTgId }),
    });
  },

  lobbyChallenges(limit = 25, offset = 0): Promise<LobbyChallenge[]> {
    return request<LobbyChallenge[]>(`/lobby/challenges?limit=${limit}&offset=${offset}`);
  },

  searchPlayers(q: string): Promise<DirectoryEntry[]> {
    return request<DirectoryEntry[]>(`/lobby/search?q=${encodeURIComponent(q)}`);
  },

  directoryMe(): Promise<DirectoryStatus> {
    return request<DirectoryStatus>("/directory/me");
  },

  directoryOptIn(): Promise<DirectoryStatus> {
    return request<DirectoryStatus>("/directory/opt-in", { method: "POST", body: "{}" });
  },

  directoryOptOut(): Promise<{ ok: true }> {
    return request<{ ok: true }>("/directory/opt-out", { method: "POST", body: "{}" });
  },

  /** Public — renders the accept screen before login. */
  challengePreview(code: string): Promise<ChallengePreview> {
    return request<ChallengePreview>(`/challenge/${encodeURIComponent(code)}`);
  },

  acceptChallenge(code: string, boogaId: string, seed?: number): Promise<PvpFightResponse> {
    return request<PvpFightResponse>(`/challenge/${encodeURIComponent(code)}/accept`, {
      method: "POST",
      body: JSON.stringify({ boogaId, seed }),
    });
  },

  cancelChallenge(code: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/challenge/${encodeURIComponent(code)}/cancel`, {
      method: "POST",
      body: "{}",
    });
  },

  queueJoin(boogaId: string, seed?: number): Promise<QueueJoinResult> {
    return request<QueueJoinResult>("/queue/join", {
      method: "POST",
      body: JSON.stringify({ boogaId, seed }),
    });
  },

  queueLeave(): Promise<{ ok: true }> {
    return request<{ ok: true }>("/queue/leave", { method: "POST", body: "{}" });
  },

  queueStatus(): Promise<QueueStatusResult> {
    return request<QueueStatusResult>("/queue/status");
  },

  pvpLeaderboard(limit = 25, offset = 0): Promise<PvpLeaderboardEntry[]> {
    return request<PvpLeaderboardEntry[]>(`/leaderboard/pvp?limit=${limit}&offset=${offset}`);
  },

  /** Public — seed-deterministic replay from a shared deep link. */
  fightById(id: number): Promise<PvpFightResponse> {
    return request<PvpFightResponse>(`/fight/${id}`);
  },
};
